import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, CheckCircle, Clock, Target, TrendingUp, ArrowRight } from 'lucide-react';
import { ProductPricing } from '@/services/discount.service';
import { formatPrice } from '@/hooks/useFeaturedPricing';

/**
 * Props for PromptEngineeringCard component
 */
interface PromptEngineeringCardProps {
  /** Pricing data from API - if not provided, shows loading state */
  pricing?: ProductPricing | null;
  /** Whether pricing is currently loading */
  loading?: boolean;
}

/**
 * PromptEngineeringCard Component
 *
 * A reusable card component for displaying the Prompt Engineering Mastery course
 * with professional styling, hover effects, and call-to-action buttons.
 *
 * Pricing is dynamically fetched from the backend pricing API.
 *
 * @component
 * @example
 * ```tsx
 * const { promptEngineeringPricing, loading } = useFeaturedPricing();
 * <PromptEngineeringCard pricing={promptEngineeringPricing} loading={loading} />
 * ```
 */
export default function PromptEngineeringCard({ pricing, loading = false }: PromptEngineeringCardProps) {
  // Extract pricing values with fallbacks for loading state
  const displayPrice = pricing?.display_price ?? 0;
  const originalPrice = pricing?.original_price ?? 0;
  const isSaleActive = pricing?.is_sale_active ?? false;
  const savingsPercentage = pricing?.savings_percentage ?? 0;

  return (
    <div className="group relative">
      {/* Glow Effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-violet-600 rounded-3xl blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-500"></div>

      {/* Card */}
      <div className="relative bg-gradient-to-br from-white/[0.12] to-white/[0.05] backdrop-blur-xl rounded-3xl border border-white/20 group-hover:border-purple-500/50 transition-all duration-500 overflow-hidden h-full">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        <div className="relative p-8 h-full flex flex-col">
          {/* Icon & Badge */}
          <div className="flex items-start justify-between mb-6">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-2xl shadow-purple-500/50">
                <Sparkles className="w-8 h-8" strokeWidth={2.5} />
              </div>
              <div className="absolute -inset-2 bg-gradient-to-br from-purple-600 to-violet-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
            </div>
            <div className="bg-gradient-to-r from-purple-600/20 to-violet-600/20 border border-purple-500/30 px-4 py-1.5 rounded-full">
              <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                BESTSELLER
              </span>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-3xl font-bold mb-4 leading-tight group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-violet-400 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
            Prompt Engineering Mastery
          </h3>

          {/* Subtitle */}
          <p className="text-lg text-purple-300/90 mb-6 font-medium">
            Unlock the Power of AI with Expert-Level Prompts
          </p>

          {/* Description */}
          <p className="text-gray-400 mb-8 leading-relaxed flex-grow">
            Stop getting mediocre AI responses. Learn the exact frameworks and techniques that professionals use to 10x their output. Transform from AI novice to prompt engineering master.
          </p>

          {/* Key Benefits */}
          <div className="space-y-3 mb-8">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">Master 7 core prompting skills that pros use daily</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">Get 12+ proven prompt templates for instant results</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">Build your personal prompt library worth $1000s</span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="flex items-center space-x-2 bg-white/5 px-3 py-2 rounded-lg">
              <Clock className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-gray-300">7 hours</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/5 px-3 py-2 rounded-lg">
              <Target className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-gray-300">Beginner Friendly</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/5 px-3 py-2 rounded-lg">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-gray-300">Career Boost</span>
            </div>
          </div>

          {/* Pricing & CTA */}
          <div className="border-t border-white/10 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                {loading ? (
                  /* Loading skeleton */
                  <div className="space-y-2">
                    <div className="h-10 w-24 bg-white/10 rounded animate-pulse"></div>
                    <div className="h-4 w-32 bg-white/10 rounded animate-pulse"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                        {formatPrice(displayPrice)}
                      </span>
                      {isSaleActive && originalPrice > displayPrice && (
                        <span className="text-2xl text-gray-300 line-through font-medium decoration-2">
                          {formatPrice(originalPrice)}
                        </span>
                      )}
                    </div>
                    {isSaleActive && savingsPercentage > 0 && (
                      <p className="text-sm text-purple-400 mt-1">
                        Save {savingsPercentage}% - Limited Time
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <Link
              to="/prompt-engineering-mastery"
              className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-2 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 group/btn"
            >
              <span>Start Learning Now</span>
              <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
